import { Injectable } from '@angular/core';
import { Transition } from '@uirouter/core';

import { ScheduleApiService } from 'src/app/api/services/schedule-api.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { AccessLevel, SCHEDULE_ACCESS } from '../types/access-level';
import { PermissionService } from './permission.service';
import { UserStateService } from './user-state.service';

@Injectable({
  providedIn: 'root',
})
export class AccessGuardService {
  private schedulesAccessRequestId = 0;

  constructor(
    private permissionService: PermissionService,
    private userStateService: UserStateService,
    private scheduleApiService: ScheduleApiService,
    private broadcasterService: BroadcasterService,
  ) {
    this.broadcasterService.on(
      'risevision.company.selectedCompanyChanged',
      () => {
        this.schedulesAccessRequestId++;
      },
    );
  }

  requireAccess(level: AccessLevel, transition: Transition): Promise<unknown> {
    if (this.permissionService.hasAccess(level)) {
      return Promise.resolve();
    }

    return this.redirectToPresentations(transition);
  }

  requireSchedulesAccess(transition: Transition): Promise<unknown> {
    const requestId = ++this.schedulesAccessRequestId;

    if (this.permissionService.hasAccess(SCHEDULE_ACCESS.PLAYLIST_MANAGER)) {
      return Promise.resolve();
    }

    if (!this.userStateService.isAssignedPublisher()) {
      return this.redirectToPresentations(transition);
    }

    return this.scheduleApiService.list({ sortBy: 'name', count: 1 }).then(
      (result) => {
        if (requestId !== this.schedulesAccessRequestId) {
          return this.rejectStaleSchedulesAccess(transition);
        }

        if (result.items && result.items.length) {
          return;
        }

        return this.redirectToPresentations(transition);
      },
      () => {
        if (requestId !== this.schedulesAccessRequestId) {
          return this.rejectStaleSchedulesAccess(transition);
        }

        return this.redirectToPresentations(transition);
      },
    );
  }

  requireAdminAccess(transition: Transition): Promise<unknown> {
    if (this.userStateService.hasPermission('sa ua')) {
      return Promise.resolve();
    }

    return this.redirectToPresentations(transition);
  }

  requireBillingAccess(transition: Transition): Promise<unknown> {
    if (this.userStateService.canAccessBilling()) {
      return Promise.resolve();
    }

    return this.redirectToPresentations(transition);
  }

  requireAdminOrOwnUserAccess(transition: Transition): Promise<unknown> {
    const name = transition.to().name ?? '';

    if (name === 'apps.user.details' || name.startsWith('apps.user.details.')) {
      const username = transition.params().username as string | undefined;

      if (!username || this.userStateService.checkUsername(username)) {
        return Promise.resolve();
      }
    }

    return this.requireAdminAccess(transition);
  }

  private rejectStaleSchedulesAccess(transition: Transition): Promise<unknown> {
    transition.abort();
    return Promise.reject(new Error('Stale schedules access check'));
  }

  private redirectToPresentations(transition: Transition): Promise<unknown> {
    transition.abort();
    return transition.router.stateService.go('apps.editor.list');
  }
}
